import { InboxReplyTarget } from '../core/models/inbox.model';

const REPLY_START = '⟦inbox-reply⟧';
const REPLY_END = '⟦/inbox-reply⟧';
const REPLY_START_ASCII = '[inbox-reply]';
const REPLY_END_ASCII = '[/inbox-reply]';

/**
 * Monta o corpo da mensagem com o bloco de resposta embutido.
 * @param body Texto digitado
 * @param reply Destino da resposta
 */
export function encodeInboxReply(body: string, reply: InboxReplyTarget | null): string {
  const text = body.trim();
  if (!reply) {
    return text;
  }

  const author = reply.author.replace(/\n/g, ' ').trim();
  const timeLabel = reply.timeLabel.replace(/\n/g, ' ').trim();
  const quoted = reply.text.replace(/\r\n/g, '\n').trim();
  return `${REPLY_START}\n${author}\n${timeLabel}\n${quoted}\n${REPLY_END}\n${text}`;
}

/**
 * Extrai o bloco de resposta e o texto principal.
 * @param raw Texto bruto da mensagem
 */
export function decodeInboxReply(raw: string | null | undefined): { reply: InboxReplyTarget | null; text: string } {
  const value = (raw ?? '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const markers = findReplyMarkers(value);
  if (!markers) {
    return { reply: null, text: raw ?? '' };
  }

  const { start, end, startLen, endLen } = markers;
  const payload = value.slice(start + startLen, end);
  const [author = '', timeLabel = '', ...rest] = payload.replace(/^\n/, '').split('\n');
  const quoted = rest.join('\n').trim();
  const textStart = end + endLen;
  const text = value.slice(textStart).replace(/^\n/, '');

  if (!author.trim() || !quoted) {
    return { reply: null, text: raw ?? '' };
  }

  return {
    reply: {
      messageId: '',
      author: author.trim(),
      text: quoted,
      timeLabel: timeLabel.trim(),
    },
    text,
  };
}

/**
 * Formata data/hora da mensagem para o preview de resposta.
 * @param createdAt ISO da mensagem
 * @param fallbackTime Hora curta já exibida na bolha
 */
export function formatInboxReplyTime(createdAt: string | undefined, fallbackTime: string): string {
  if (!createdAt) {
    return fallbackTime;
  }

  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/Sao_Paulo',
    })
      .format(new Date(createdAt))
      .replace(',', '');
  } catch {
    return fallbackTime;
  }
}

/**
 * Localiza o bloco de resposta no texto (delimitadores unicode ou ASCII).
 */
function findReplyMarkers(
  value: string
): { start: number; end: number; startLen: number; endLen: number } | null {
  const pairs: Array<{ startToken: string; endToken: string }> = [
    { startToken: REPLY_START, endToken: REPLY_END },
    { startToken: REPLY_START_ASCII, endToken: REPLY_END_ASCII },
  ];

  for (const { startToken, endToken } of pairs) {
    if (!value.startsWith(startToken)) {
      continue;
    }

    const endNeedle = `\n${endToken}`;
    const end = value.indexOf(endNeedle, startToken.length);
    if (end < 0) {
      continue;
    }

    return {
      start: 0,
      end,
      startLen: startToken.length,
      endLen: endNeedle.length,
    };
  }

  return null;
}
